/**
 * Functional component for
 */

//react hooks
import { useCallback, useEffect, useMemo, useState } from "react";

//firebase
import { onAuthStateChanged } from "firebase/auth";
import {
  collection, collectionGroup, getDocs, orderBy, query,
} from "firebase/firestore";
import { db, auth } from "../../../app/firebase";

// services
import { adminDeleteUsers } from "../../../services/adminUsersService";

//componenets
import AdminUsers from "./adminUsers";
import {
  USER_ACTIVITY_COLUMNS,
  calculateLastActivity,
  emptyActivity,
  getTimestampValue,
  latestActivityColumn,
  sumPetActivity,
  summarizeActivityDocs,
} from "./adminUserActivity";

const PAGE_SIZE = 30; // Размер страницы для отображения
const MAX_DELETE_BATCH = 20;
const USER_FETCH_CONCURRENCY = 4;

async function mapPool(items, limit, fn) {
  const results = new Array(items.length);
  let nextIndex = 0;

  async function worker() {
    while (nextIndex < items.length) {
      const index = nextIndex;
      nextIndex += 1;
      results[index] = await fn(items[index], index);
    }
  }

  const workers = Array.from(
    { length: Math.min(limit, items.length) },
    () => worker(),
  );
  await Promise.all(workers);
  return results;
}

async function fetchActivityForPet(userId, petId) {
  const activity = emptyActivity();
  const activityKeys = {};
  const activityLatestByKey = {};
  const dates = [];

  await Promise.all(
    USER_ACTIVITY_COLUMNS.map(async (col) => {
      try {
        const snap = await getDocs(
          collection(db, "users", userId, "pets", petId, col.collection),
        );
        const summary = summarizeActivityDocs(snap.docs, col);
        activity[col.key] = summary.count;
        if (summary.keys) activityKeys[col.key] = summary.keys;
        if (summary.latest) {
          dates.push(summary.latest);
          activityLatestByKey[col.key] = summary.latest;
        }
      } catch (e) {
        console.error(
          `Error fetching ${col.collection} for pet ${petId}:`,
          e,
        );
      }
    }),
  );

  return {
    activity,
    activityKeys,
    activityLatestByKey,
    activityLatest: dates.length
      ? new Date(Math.max(...dates.map((d) => d.getTime())))
      : null,
  };
}

async function fetchPetsForUser(userId) {
  try {
    const snap = await getDocs(collection(db, "users", userId, "pets"));
    return Promise.all(
      snap.docs.map(async (d) => {
        const petData = d.data();
        const activity = await fetchActivityForPet(userId, d.id);
        return {
          id: d.id,
          name: petData.name,
          type: petData.type,
          customTypeName: petData.customTypeName,
          createdAt: petData.createdAt,
          updatedAt: petData.updatedAt,
          ...activity,
        };
      }),
    );
  } catch (e) {
    console.error(`Error fetching pets for user ${userId}:`, e);
    return [];
  }
}

function userPetFromPath(path) {
  const parts = path.split("/");
  if (parts.length !== 4 || parts[0] !== "users" || parts[2] !== "pets") return null;
  return { userId: parts[1], petId: parts[3] };
}

function petActivityFromPath(path, collectionName) {
  const parts = path.split("/");
  if (
    parts.length !== 6 ||
    parts[0] !== "users" ||
    parts[2] !== "pets" ||
    parts[4] !== collectionName
  ) {
    return null;
  }
  return { userId: parts[1], petId: parts[3] };
}

function userDirectoryFromPath(path, collectionName) {
  const parts = path.split("/");
  if (parts.length !== 4 || parts[0] !== "users" || parts[2] !== collectionName) {
    return null;
  }
  return { userId: parts[1] };
}

function applyDirectoryCounts(user, directoriesByKey) {
  const activity = { ...(user.activity || emptyActivity()) };
  let lastActivityColumn = user.lastActivityColumn;
  let lastActivityColumnAt = user.lastActivityColumnAt
    ? new Date(user.lastActivityColumnAt)
    : null;
  let lastActivity = user.lastActivity ? new Date(user.lastActivity) : null;

  Object.entries(directoriesByKey || {}).forEach(([key, info]) => {
    if (!info?.count) return;
    if (info.count > (activity[key] || 0)) activity[key] = info.count;
    if (!info.latest) return;
    const time = info.latest.getTime();
    if (!lastActivity || time > lastActivity.getTime()) lastActivity = info.latest;
    const columnTime = lastActivityColumnAt ? lastActivityColumnAt.getTime() : 0;
    if (time > columnTime) {
      lastActivityColumn = key;
      lastActivityColumnAt = info.latest;
    }
  });

  return {
    ...user,
    activity,
    lastActivity,
    lastActivityColumn,
    lastActivityColumnAt,
  };
}

function loadCollectionGroupSnaps() {
  const userCollections = [
    ...new Set(
      USER_ACTIVITY_COLUMNS.map((col) => col.userCollection).filter(Boolean),
    ),
  ];

  return Promise.all([
    getDocs(collectionGroup(db, "pets")),
    Promise.all(
      USER_ACTIVITY_COLUMNS.map(async (col) => ({
        col,
        snap: await getDocs(collectionGroup(db, col.collection)),
      })),
    ),
    Promise.all(
      userCollections.map(async (name) => ({
        name,
        snap: await getDocs(collectionGroup(db, name)),
      })),
    ),
  ]);
}

/** One read per folder for the whole database, instead of one read per pet. */
async function attachPetsFromCollectionGroups(items, preloadedSnaps) {
  const [petsSnap, activitySnaps, directorySnaps] = preloadedSnaps ||
    await loadCollectionGroupSnaps();

  const docsByPet = new Map();
  activitySnaps.forEach(({ col, snap }) => {
    snap.docs.forEach((docSnap) => {
      const ids = petActivityFromPath(docSnap.ref.path, col.collection);
      if (!ids) return;
      const petKey = `${ids.userId}/${ids.petId}`;
      if (!docsByPet.has(petKey)) docsByPet.set(petKey, {});
      const bucket = docsByPet.get(petKey);
      if (!bucket[col.key]) bucket[col.key] = [];
      bucket[col.key].push(docSnap);
    });
  });

  const petsByUser = new Map();
  petsSnap.docs.forEach((docSnap) => {
    const ids = userPetFromPath(docSnap.ref.path);
    if (!ids) return;
    const petData = docSnap.data() || {};
    const petKey = `${ids.userId}/${ids.petId}`;
    const buckets = docsByPet.get(petKey) || {};
    const activity = emptyActivity();
    const activityKeys = {};
    const activityLatestByKey = {};
    const dates = [];

    USER_ACTIVITY_COLUMNS.forEach((col) => {
      const summary = summarizeActivityDocs(buckets[col.key] || [], col);
      activity[col.key] = summary.count;
      if (summary.keys) activityKeys[col.key] = summary.keys;
      if (summary.latest) {
        dates.push(summary.latest);
        activityLatestByKey[col.key] = summary.latest;
      }
    });

    const pet = {
      id: ids.petId,
      name: petData.name,
      type: petData.type,
      customTypeName: petData.customTypeName,
      createdAt: petData.createdAt,
      updatedAt: petData.updatedAt,
      activity,
      activityKeys,
      activityLatestByKey,
      activityLatest: dates.length
        ? new Date(Math.max(...dates.map((d) => d.getTime())))
        : null,
    };
    if (!petsByUser.has(ids.userId)) petsByUser.set(ids.userId, []);
    petsByUser.get(ids.userId).push(pet);
  });

  const directoryByUser = new Map();
  directorySnaps.forEach(({ name, snap }) => {
    const columns = USER_ACTIVITY_COLUMNS.filter((col) => col.userCollection === name);
    const docsByUser = new Map();
    snap.docs.forEach((docSnap) => {
      const ids = userDirectoryFromPath(docSnap.ref.path, name);
      if (!ids) return;
      if (!docsByUser.has(ids.userId)) docsByUser.set(ids.userId, []);
      docsByUser.get(ids.userId).push(docSnap);
    });
    docsByUser.forEach((docs, userId) => {
      if (!directoryByUser.has(userId)) directoryByUser.set(userId, {});
      const target = directoryByUser.get(userId);
      const summary = summarizeActivityDocs(docs, { kind: "any" });
      columns.forEach((col) => {
        target[col.key] = { count: summary.count, latest: summary.latest };
      });
    });
  });

  return items.map((user) => {
    const pets = petsByUser.get(user.id) || [];
    const latestColumn = latestActivityColumn(pets);
    const withPets = {
      ...user,
      pets,
      activity: sumPetActivity(pets),
      lastActivityColumn: latestColumn?.key || null,
      lastActivityColumnAt: latestColumn?.at || null,
    };
    const withActivity = {
      ...withPets,
      lastActivity: calculateLastActivity(withPets),
    };
    return applyDirectoryCounts(withActivity, directoryByUser.get(user.id));
  });
}

async function attachPets(items) {
  const petsArrays = await mapPool(items, USER_FETCH_CONCURRENCY, (u) =>
    fetchPetsForUser(u.id),
  );
  const specialistDirectories = await mapPool(items, USER_FETCH_CONCURRENCY, async (u) => {
    try {
      const snap = await getDocs(collection(db, "users", u.id, "specialistDirectory"));
      return summarizeActivityDocs(snap.docs, { kind: "any" });
    } catch (e) {
      console.error(`Error fetching specialistDirectory for user ${u.id}:`, e);
      return null;
    }
  });
  return items.map((u, i) => {
    const pets = petsArrays[i] || [];
    const latestColumn = latestActivityColumn(pets);
    const userWithPets = {
      ...u,
      pets,
      activity: sumPetActivity(pets),
      lastActivityColumn: latestColumn?.key || null,
      lastActivityColumnAt: latestColumn?.at || null,
    };
    const withActivity = {
      ...userWithPets,
      lastActivity: calculateLastActivity(userWithPets),
    };
    const directory = specialistDirectories[i];
    return applyDirectoryCounts(
      withActivity,
      directory ? { petContacts: { count: directory.count, latest: directory.latest } } : null,
    );
  });
}

/**
 * Family links for the Email column: a member gets the owner's email,
 * an owner gets the members' emails. Search still matches only the own email.
 */
function attachFamilyEmails(users) {
  const emailById = new Map(users.map((u) => [u.id, u.email || u.id]));
  const membersByOwner = new Map();
  users.forEach((u) => {
    if (typeof u.familyOwnerId !== "string" || !u.familyOwnerId) return;
    if (!membersByOwner.has(u.familyOwnerId)) membersByOwner.set(u.familyOwnerId, []);
    membersByOwner.get(u.familyOwnerId).push(u.email || u.id);
  });
  return users.map((u) => {
    const ownerId = typeof u.familyOwnerId === "string" ? u.familyOwnerId : "";
    const familyOwnerEmail = ownerId ? emailById.get(ownerId) || ownerId : null;
    const familyMemberEmails = membersByOwner.get(u.id) || [];
    if (!familyOwnerEmail && familyMemberEmails.length === 0) return u;
    return { ...u, familyOwnerEmail, familyMemberEmails };
  });
}

export default function AdminUsersContainer() {
  const [checkingAdmin, setCheckingAdmin] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);

  const [allUsers, setAllUsers] = useState([]); // Все загруженные пользователи
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [displayedCount, setDisplayedCount] = useState(PAGE_SIZE); // Сколько показываем

  const [search, setSearch] = useState("");
  const [sort, setSort] = useState({ key: "lastActivity", dir: "desc" });
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      setCheckingAdmin(true);
      setIsAdmin(false);
      setCurrentUserId(user?.uid || null);
      if (!user) { setCheckingAdmin(false); return; }
      try {
        const token = await user.getIdTokenResult(true);
        setIsAdmin(!!token.claims.admin);
      } catch (e) {
        console.error(e);
      } finally {
        setCheckingAdmin(false);
      }
    });
    return () => unsub();
  }, []);
  

  // Загружаем всех пользователей, затем сортируем по lastActivity
  const loadAllUsers = async () => {
    if (!isAdmin || loading) return;
    setLoading(true);
    setError(""); 
    try {
      const groupsPromise = loadCollectionGroupSnaps().catch((groupError) => {
        console.warn(
          "Admin users: collection group load failed, reading per pet",
          groupError,
        );
        return null;
      });
      const q = query(collection(db, "users"), orderBy("email"));
      const snap = await getDocs(q);
      
      const baseItems = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
        pets: [],
        activity: emptyActivity(),
      }));
      setAllUsers(baseItems);
      setDisplayedCount(PAGE_SIZE);
      setSelectedIds(new Set());

      const groupedSnaps = await groupsPromise;
      const itemsWithPets = groupedSnaps
        ? await attachPetsFromCollectionGroups(baseItems, groupedSnaps)
        : await attachPets(baseItems);

      // Сортируем по lastActivity (самые активные сверху)
      const sorted = [...itemsWithPets].sort((a, b) => {
        const dateA = a.lastActivity ? a.lastActivity.getTime() : 0;
        const dateB = b.lastActivity ? b.lastActivity.getTime() : 0;
        return dateB - dateA; // descending order
      });

      setAllUsers(sorted);
      setDisplayedCount(PAGE_SIZE); // Показываем первые PAGE_SIZE
      setSelectedIds(new Set());
    } catch (e) {
      console.error(e);
      setError(e.message || "Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (isAdmin) loadAllUsers(); }, [isAdmin]); // eslint-disable-line

  // Семья: участники ссылаются на владельца через familyOwnerId (без лишних чтений)
  const usersWithFamily = useMemo(() => attachFamilyEmails(allUsers), [allUsers]);

  // Фильтрация и сортировка (сортировка уже сделана при загрузке, но для поиска нужна фильтрация)
  const filtered = useMemo(() => {
    let result = usersWithFamily;
    
    // Apply search filter
    if (search.trim()) {
      const s = search.trim().toLowerCase();
      result = result.filter((u) =>
        (u.email || "").toLowerCase().includes(s) ||
        (u.pets || []).some((p) =>
          (p.name || "").toLowerCase().includes(s) ||
          (p.type || "").toLowerCase().includes(s) ||
          (p.customTypeName || "").toLowerCase().includes(s)
        )
      );
    }
    
    const dir = sort.dir === "asc" ? 1 : -1;
    return [...result].sort((a, b) => {
      const aTime = getTimestampValue(a[sort.key])?.getTime() || 0;
      const bTime = getTimestampValue(b[sort.key])?.getTime() || 0;
      if (!aTime && !bTime) return 0;
      if (!aTime) return 1;
      if (!bTime) return -1;
      return (aTime - bTime) * dir;
    });
  }, [usersWithFamily, search, sort]);

  const handleSort = useCallback((key) => {
    setSort((prev) => (
      prev.key === key
        ? { key, dir: prev.dir === "desc" ? "asc" : "desc" }
        : { key, dir: "desc" }
    ));
    setDisplayedCount(PAGE_SIZE);
  }, []);

  // Пагинация на клиенте - показываем только часть записей
  const displayedRows = useMemo(() => {
    return filtered.slice(0, displayedCount);
  }, [filtered, displayedCount]);

  const handleLoadMore = () => {
    setDisplayedCount(prev => prev + PAGE_SIZE);
  };

  const isDone = displayedCount >= filtered.length;

  const handleToggleSelect = useCallback((userId) => {
    if (userId === currentUserId) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  }, [currentUserId]);

  const handleToggleSelectAllVisible = useCallback(() => {
    const selectable = displayedRows
      .map((u) => u.id)
      .filter((id) => id && id !== currentUserId);

    setSelectedIds((prev) => {
      const allSelected =
        selectable.length > 0 && selectable.every((id) => prev.has(id));
      if (allSelected) {
        const next = new Set(prev);
        selectable.forEach((id) => next.delete(id));
        return next;
      }
      const next = new Set(prev);
      selectable.forEach((id) => next.add(id));
      return next;
    });
  }, [displayedRows, currentUserId]);

  const handleClearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const handleDeleteSelected = useCallback(async () => {
    const ids = Array.from(selectedIds).filter((id) => id !== currentUserId);
    if (ids.length === 0) return;

    setDeleting(true);
    setError("");
    try {
      const deletedOk = [];
      const failures = [];

      for (let i = 0; i < ids.length; i += MAX_DELETE_BATCH) {
        const chunk = ids.slice(i, i + MAX_DELETE_BATCH);
        const { results } = await adminDeleteUsers(chunk);
        (results || []).forEach((r) => {
          if (r.ok) deletedOk.push(r.userId);
          else failures.push(r);
        });
      }

      if (deletedOk.length > 0) {
        const deletedSet = new Set(deletedOk);
        setAllUsers((prev) => prev.filter((u) => !deletedSet.has(u.id)));
        setSelectedIds((prev) => {
          const next = new Set(prev);
          deletedOk.forEach((id) => next.delete(id));
          return next;
        });
      }

      if (failures.length > 0) {
        const detail = failures
          .slice(0, 3)
          .map((f) => `${f.userId}: ${f.error || "failed"}`)
          .join("; ");
        setError(
          `Deleted ${deletedOk.length}, failed ${failures.length}. ${detail}`,
        );
      }
    } catch (e) {
      console.error(e);
      setError(e.message || "Failed to delete users");
    } finally {
      setDeleting(false);
    }
  }, [selectedIds, currentUserId]);

  const selectedUsers = useMemo(
    () => allUsers.filter((u) => selectedIds.has(u.id)),
    [allUsers, selectedIds],
  );

  if (checkingAdmin) return <div style={{ padding: 16 }}>Проверяем права…</div>;
  if (!isAdmin) return <div style={{ padding: 16 }}>403 — доступ только для администраторов</div>;

  return (
    <AdminUsers
      rows={displayedRows}
      totalUsers={allUsers.length}
      loading={loading}
      error={error}
      done={isDone}
      search={search}
      onSearchChange={setSearch}
      sortKey={sort.key}
      sortDir={sort.dir}
      onSort={handleSort}
      onRefresh={loadAllUsers}
      onLoadMore={handleLoadMore}
      selectedIds={selectedIds}
      selectedUsers={selectedUsers}
      currentUserId={currentUserId}
      deleting={deleting}
      onToggleSelect={handleToggleSelect}
      onToggleSelectAllVisible={handleToggleSelectAllVisible}
      onClearSelection={handleClearSelection}
      onDeleteSelected={handleDeleteSelected}
    />
  );
}
